<script setup lang="ts">
import type { TOrganizationAsideContacts } from '~/components/widget/organization-aside-contacts'
import { LucideGlobe, LucideLandmark, LucideMail, LucidePhone } from 'lucide-vue-next'
import type { IResponseData } from '~/types'
import { WidgetDepositProtection } from '~/components/widget/deposit-protection'

const props = withDefaults(
  defineProps<{
    organizationSlug?: string
    type?: 'banks' | 'microcredit' | 'insurance' | 'money-transfer'
    organization?: TOrganizationAsideContacts
    enableLink?: boolean
  }>(),
  {
    organizationSlug: '',
    type: 'banks',
    organization: () => ({}) as TOrganizationAsideContacts,
    enableLink: true
  }
)

const apiUrl = computed(() => {
  if (!props.organization && props.organizationSlug && props.type) {
    return `/${props.type}/${props.organizationSlug}`
  }
  return null
})

const data = ref<IResponseData<TOrganizationAsideContacts> | null>(null)
const status = ref<string | null>(null)

if (!props.organization && apiUrl.value) {
  const apiResult = useApi<IResponseData<TOrganizationAsideContacts>>(() => apiUrl.value!, {
    key: `organization-aside-contacts-block-${props.organizationSlug}`
  })
  data.value = apiResult.data.value
  status.value = apiResult.status.value
}

const info = computed((): TOrganizationAsideContacts | null => {
  return props.organization ?? data.value?.data ?? null
})

// An organization without url gets no link: a link without a target renders
// as <a> with no href, which crawlers cannot follow
const hasLink = computed(() => props.enableLink && Boolean(info.value?.url))

const { splitPhones } = usePhone()
const callCenterPhones = computed(() => splitPhones(info.value?.call_center_phone))
</script>

<template>
  <SidebarBlock v-if="info" variant="white">
    <div class="flex items-center gap-3">
      <NuxtLinkLocale v-if="hasLink" :to="info.url" class="rounded-lg border p-1">
        <NuxtImg :src="info.logo" :alt="info.title" class="h-14 w-14 min-w-14" />
      </NuxtLinkLocale>
      <figure v-else class="rounded-lg border p-1">
        <NuxtImg :src="info.logo" :alt="info.title" class="h-14 w-14 min-w-14" />
      </figure>
      <div>
        <Heading v-if="hasLink" tag="h2" class="h3 font-serif !text-base !leading-4"
          ><NuxtLinkLocale :to="info.url" class="hover:underline">{{
            info.title
          }}</NuxtLinkLocale></Heading
        >
        <Heading v-else tag="h2" class="h3 font-serif !text-base !leading-4">{{
          info.title
        }}</Heading>
        <p class="mt-1 text-xs leading-3 text-gray-700">{{ info.official_title }}</p>
      </div>
    </div>

    <div class="mt-4 grid grid-cols-1 space-y-4">
      <div class="flex flex-col divide-y divide-gray-100 rounded-lg">
        <template v-if="callCenterPhones.length">
          <div class="flex items-center gap-3 py-3">
            <div class="ContactIcon">
              <LucidePhone class="h-5 w-5" />
            </div>
            <div>
              <p class="text-xs font-medium uppercase leading-tight text-gray-500">
                {{ $t('common.phone') }}
              </p>
              <template v-for="(phone, i) in callCenterPhones" :key="phone.tel">
                <a
                  :href="`tel:${phone.tel}`"
                  class="whitespace-nowrap text-sm font-semibold leading-tight hover:text-blue-600"
                  >{{ phone.label }}</a
                ><span v-if="i < callCenterPhones.length - 1" class="text-sm">, </span>
              </template>
            </div>
          </div>
        </template>

        <template v-if="info.email">
          <a
            :href="`mailto:${info.email}`"
            class="flex items-center gap-3 py-3 hover:text-blue-600"
          >
            <div class="ContactIcon">
              <LucideMail class="h-5 w-5" />
            </div>
            <div>
              <p class="text-xs font-medium uppercase leading-tight text-gray-500">
                {{ $t('common.email') }}
              </p>
              <span class="text-sm font-semibold leading-tight">{{ info.email }}</span>
            </div>
          </a>
        </template>

        <template v-if="info.website_url">
          <a
            :href="info.website_url"
            class="flex items-center gap-3 py-3 hover:text-blue-600"
            target="_blank"
          >
            <div class="ContactIcon">
              <LucideGlobe class="h-5 w-5" />
            </div>
            <div>
              <p class="text-xs font-medium uppercase leading-tight text-gray-500">
                {{ $t('common.website') }}
              </p>
              <span class="text-sm font-semibold leading-tight">{{ info.website_url }}</span>
            </div>
          </a>
        </template>

        <template v-if="info.head_branch_location">
          <div class="flex items-center gap-3 py-3">
            <div class="ContactIcon">
              <LucideLandmark class="h-5 w-5" />
            </div>
            <div>
              <p class="text-xs font-medium uppercase leading-tight text-gray-500">
                {{ $t('common.legal_address') }}
              </p>
              <span class="text-sm font-semibold leading-tight">{{
                info.head_branch_location
              }}</span>
            </div>
          </div>
        </template>

        <template v-if="info.telegram">
          <a :href="info.telegram" class="group flex items-center gap-3 py-3" target="_blank">
            <div class="ContactIcon group-hover:text-[#2AABEE] [&>svg]:size-5">
              <UiIcon name="telegram" />
            </div>
            <div>
              <p class="text-xs font-medium uppercase leading-tight text-gray-500">
                {{ $t('common.telegram') }}
              </p>
              <span class="text-sm font-semibold group-hover:text-[#2AABEE]">{{
                info.telegram
              }}</span>
            </div>
          </a>
        </template>

        <template v-if="info.whatsapp">
          <a :href="info.whatsapp" class="group flex items-center gap-3 py-3" target="_blank">
            <div class="ContactIcon group-hover:text-[#25D366] [&>svg]:size-5">
              <UiIcon name="whatsapp" />
            </div>
            <div>
              <p class="text-xs font-medium uppercase leading-tight text-gray-500">
                {{ $t('common.whatsapp') }}
              </p>
              <span class="text-sm font-semibold group-hover:text-[#25D366]">{{
                info.whatsapp
              }}</span>
            </div>
          </a>
        </template>
      </div>
      <WidgetDepositProtection :organization="info" />
    </div>
  </SidebarBlock>

  <SidebarBlock v-else-if="status !== 'success'" variant="white">
    <div class="animate-pulse space-y-5">
      <div class="flex items-center gap-3">
        <div class="h-7 w-7 rounded-lg bg-gray-200" />
        <div class="flex-1 space-y-2">
          <div class="h-3 w-2/3 rounded bg-gray-200" />
          <div class="h-2 w-1/3 rounded bg-gray-200" />
        </div>
      </div>

      <div class="mt-4 space-y-3">
        <div class="flex items-center gap-2">
          <div class="size-9 rounded-lg bg-gray-200" />
          <div class="h-3 w-1/2 rounded bg-gray-200" />
        </div>
        <div class="flex items-center gap-2">
          <div class="size-9 rounded-lg bg-gray-200" />
          <div class="h-3 w-2/3 rounded bg-gray-200" />
        </div>
        <div class="flex items-center gap-2">
          <div class="size-9 rounded-lg bg-gray-200" />
          <div class="h-3 w-1/3 rounded bg-gray-200" />
        </div>
      </div>
    </div>
  </SidebarBlock>
</template>
